import { AUTH_QUERY_PARAMS } from '@/lib/constants';

/**
 * Query and fragment parameters whose values are bearer credentials or single-use sign-in codes.
 * Analytics, replays and logs must never keep their values.
 */
const SENSITIVE_URL_PARAMS = [
  // Supabase session material, named as auth-js names it in OAuth callbacks.
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'token',
  'token_hash',
  // OAuth authorization codes. The callback URL can outlive its request as the next page's referrer.
  'code',
  AUTH_QUERY_PARAMS.CLI_CODE,
  AUTH_QUERY_PARAMS.DESKTOP_CODE,
] as const;

const REDACTED_VALUE = 'redacted';

const SENSITIVE_NAMES = SENSITIVE_URL_PARAMS.join('|');

// A parameter of the URL itself: a delimiter, a sensitive name, `=`, then everything up to the next
// raw delimiter, so an encoded `&` or `#` inside the value is redacted with it. `;` covers
// `;`-separated params and HTML-escaped `&amp;`.
const TOP_LEVEL_PARAM = new RegExp(`(^|[?&#;])(${SENSITIVE_NAMES})(=|%3D)[^&#;\\s"'<>]*`, 'gi');

// A parameter of a URL nested percent-encoded in another URL's query (a returnTo, a redirect's
// referrer), scrubbed without decoding it. Its value ends at the next encoded or raw delimiter.
const NESTED_PARAM = new RegExp(
  `(%3F|%26|%23)(${SENSITIVE_NAMES})(=|%3D)[^&#;\\s"'<>]*?(?=%26|%23|[&#;\\s"'<>]|$)`,
  'gi',
);

// Stands in for a reference back to an object the walk is already inside. Returning that object
// would put its unscrubbed original into the result.
const CIRCULAR_REFERENCE = '[circular]';

/**
 * Replaces the value of every sensitive parameter in `value` with `redacted`, in the query and the
 * fragment. Works on any string, not only parseable URLs, and leaves every other parameter (for
 * example `gclid` and `utm_source`) byte for byte.
 */
export function scrubSensitiveUrlParams(value: string): string {
  const redaction = `$1$2$3${REDACTED_VALUE}`;
  return value.replace(TOP_LEVEL_PARAM, redaction).replace(NESTED_PARAM, redaction);
}

/** For analytics SDK hooks that hand over an event carrying the page URL, like Vercel's. */
export function scrubEventUrl<T extends { url: string }>(event: T): T {
  return { ...event, url: scrubSensitiveUrlParams(event.url) };
}

/**
 * Scrubs every string reachable through arrays and plain objects, keys included, and returns a new
 * structure. A reference back to an enclosing object becomes `[circular]`. Other values come back
 * unchanged.
 */
export function scrubSensitiveUrlParamsDeep<T>(value: T): T {
  return scrubValue(value, new WeakSet()) as T;
}

// `ancestors` holds the objects on the current path only, so a cycle stops the walk while an object
// shared by two branches is still scrubbed in both.
function scrubValue(value: unknown, ancestors: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrubSensitiveUrlParams(value);
  }
  if (typeof value !== 'object' || value === null) {
    return value;
  }
  if (ancestors.has(value)) {
    return CIRCULAR_REFERENCE;
  }
  const prototype = Object.getPrototypeOf(value);
  if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) {
    return value;
  }

  ancestors.add(value);
  const scrubbed = Array.isArray(value)
    ? value.map((item) => scrubValue(item, ancestors))
    : Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
          scrubSensitiveUrlParams(key),
          scrubValue(item, ancestors),
        ]),
      );
  ancestors.delete(value);
  return scrubbed;
}
